/**
 * usePortalAccess (OCP-570) - "may THIS member spend money, change
 * infrastructure or edit the billing profile?".
 *
 * The API is the enforcement (`apps/api/src/lib/portal-role-gates.ts`):
 *
 *   infra_write_requires_admin     owner, admin           AI keys, instance
 *                                                         API keys, custom
 *                                                         domain, licence
 *                                                         revoke; since
 *                                                         OCP-929 also the
 *                                                         licence file and
 *                                                         backup / export
 *                                                         downloads (scope
 *                                                         `export` below)
 *   contract_requires_admin        owner, admin           checkout, code
 *                                                         redeem, free plan
 *   billing_requires_billing_role  owner, admin, billing  billing profile,
 *                                                         payment method link
 *
 * This hook is the matching UX so a read-only member is told up front
 * instead of after clicking - the same split as the Mail surface
 * (`use-mail-write-access.ts`, OCP-545): hiding a control is a courtesy, the
 * 403 is the rule. Keep the lists in lock-step with the API module; if they
 * disagree the server wins and the member sees an error, never an unsafe
 * action.
 */
import type { PortalUserRole } from './auth-context';
import { useOptionalAuth } from './auth-context';
import { ROLE_LABEL } from './use-mail-write-access';

export const INFRA_WRITE_ROLES: ReadonlyArray<PortalUserRole> = ['owner', 'admin'];
export const CONTRACT_ROLES: ReadonlyArray<PortalUserRole> = ['owner', 'admin'];
export const BILLING_ROLES: ReadonlyArray<PortalUserRole> = ['owner', 'admin', 'billing-only'];

export type PortalAccessScope = 'infra' | 'contract' | 'billing' | 'export';

const SCOPE_COPY: Record<PortalAccessScope, string> = {
  infra: 'Only an owner or administrator can change keys, domains and licences.',
  contract: 'Only an owner or administrator can order plans, redeem codes or activate a plan for this account.',
  billing: 'Only an owner, administrator or billing member can change the billing details.',
  // OCP-929 - same role set as infra (the API gate is requireInfraWriteRole):
  // a licence file is the licence key, an archive is the workspace's data.
  export: 'Only an owner or administrator can download licence files, backups and exports.',
};

export interface PortalAccess {
  canManageInfra: boolean;
  /** OCP-929 - may download licence files, backup archives and full exports (owner, admin). */
  canExportData: boolean;
  canConcludeContract: boolean;
  canManageBilling: boolean;
  role: PortalUserRole | null;
  /** Ready-made copy for the read-only banner of a scope, null when allowed or unknown. */
  readOnlyReason(scope: PortalAccessScope): string | null;
}

export function usePortalAccess(): PortalAccess {
  const auth = useOptionalAuth();
  const role = auth?.user?.role ?? null;
  // Unknown role (session still resolving) leaves the controls in place: the
  // API is the gate, the worst case is a 403 banner instead of a hidden button.
  const canManageInfra = role === null || INFRA_WRITE_ROLES.includes(role);
  const canConcludeContract = role === null || CONTRACT_ROLES.includes(role);
  const canManageBilling = role === null || BILLING_ROLES.includes(role);
  const allowed: Record<PortalAccessScope, boolean> = {
    infra: canManageInfra,
    contract: canConcludeContract,
    billing: canManageBilling,
    export: canManageInfra,
  };
  return {
    canManageInfra,
    canExportData: canManageInfra,
    canConcludeContract,
    canManageBilling,
    role,
    readOnlyReason: (scope) =>
      allowed[scope] || role === null ? null : `${SCOPE_COPY[scope]} Your role (${ROLE_LABEL[role]}) can view it.`,
  };
}
